/**
 * InvoiceRow — one monthly-flight row plus its expandable detail.
 *
 * The main row is always display-only (stable, no accidental edits). To edit,
 * turn on Edit mode and expand the row: the detail panel becomes a form. All
 * edits flow into a per-row draft (managed by the dashboard) and are saved with
 * an explicit per-row Save. Billable Total + Status inputs are locked unless
 * the user has the accountant (invoicing.billing) permission.
 */

import React from 'react';
import { StatusPill, PacingBar, Spinner } from '../ui';
import type { StatusVariant } from '../ui';
import DeliveryPacingPanel, { PacingGlance } from './LineItemPacing';
import { fmtDateRange, fmtAsOf } from './dateFormat';
import { gamSyncState, GamSyncState } from './gamSync';
import { Invoice, InvoicePatch, GamLinkSuggestion, isConfirmed, isThreeP } from './types';

interface Props {
  invoice: Invoice;
  expanded: boolean;
  onToggle: () => void;
  editMode: boolean;
  canBill: boolean;
  canManage: boolean;
  /**
   * Immediate single-field PATCH (3P toggle / inline 3P entry / quick
   * confirm) — no draft. Resolves false on failure so callers can revert
   * optimistic local state.
   */
  onQuickPatch: (patch: InvoicePatch) => Promise<boolean>;
  draft: InvoicePatch;
  isDirty: boolean;
  saving: boolean;
  onDraftChange: (field: keyof InvoicePatch, value: unknown) => void;
  onSaveRow: () => void;
  onRevertRow: () => void;
  onMarkReviewed: () => void;
  /** Number of main-row columns, for the detail row's colSpan. */
  colCount: number;
  /** "Why not synced" reason from the last GAM auto-link (shown when unpaced). */
  diagnostic?: string;
  /**
   * A single-order fuzzy GAM match from the last auto-link that a human can
   * confirm with one click (rendered in the pacing cell for unlinked rows).
   */
  suggestion?: GamLinkSuggestion;
  /** Accept `suggestion` — links the order's line items and starts pacing. */
  onAcceptSuggestion?: () => void;
  /** True while this month's invoice PDF is generating. */
  generating?: boolean;
  /** Generate (or regenerate) this month's invoice PDF. */
  onGenerate?: () => void;
  /** Download this month's already-generated invoice PDF. */
  onDownload?: () => void;
  /** Kick off a background refresh of this month's true delivered impressions. */
  onRefreshDelivered?: () => void;
  /** True while a delivered refresh is in flight for this row (disables the button). */
  refreshingDelivered?: boolean;
}

// Billing figures keep their cents (a $33,333.33 billable must not render as
// $33,333 under a group header that shows $33,333.33); integers stay clean.
const fmtMoney = (n: number | undefined) =>
  n === undefined
    ? '—'
    : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: Number.isInteger(n) ? 0 : 2 })}`;
const fmtInt = (n: number | undefined) => (n === undefined ? '—' : Math.round(n).toLocaleString());
const fmtRate = (r?: { cpm?: number; flatRate?: number }) => {
  const n = r?.cpm ?? r?.flatRate;
  return n === undefined ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

function statusVariant(status?: string): StatusVariant {
  const s = (status || '').toLowerCase();
  // Negated forms first: "Unpaid" contains "paid" and "Not confirmed" contains
  // "confirm" — they must not get the positive pill (isConfirmed already gates
  // the actual invoice generation; this keeps the pill visually consistent).
  if (/\b(?:not|un)[^a-z0-9]*paid\b|\b(?:not|to\s+be|un|pending|awaiting|cannot|can't)[^a-z0-9]*confirm/.test(s)) return 'pending';
  if (/\bpaid\b|complete|closed/.test(s)) return 'approved';
  if (/invoiced|sent|billed|confirm/.test(s)) return 'live';
  if (/pending|review|hold|progress|active|delivering/.test(s)) return 'pending';
  if (/cancel|reject|void|dispute/.test(s)) return 'flagged';
  return 'draft';
}

const InvoiceRow: React.FC<Props> = ({
  invoice,
  expanded,
  onToggle,
  editMode,
  canBill,
  canManage,
  onQuickPatch,
  draft,
  isDirty,
  saving,
  onDraftChange,
  onSaveRow,
  onRevertRow,
  onMarkReviewed,
  colCount,
  diagnostic,
  suggestion,
  onAcceptSuggestion,
  generating,
  onGenerate,
  onDownload,
  onRefreshDelivered,
  refreshingDelivered,
}) => {
  // Effective display value = draft override (if the field is being edited)
  // else the stored value. A draft value of null means "cleared" → render as
  // empty (undefined) so the main row shows the pending blank, not the old value.
  const v = <K extends keyof InvoicePatch>(field: K, stored: InvoicePatch[K]): InvoicePatch[K] => {
    if (!(field in draft)) return stored;
    const dv = draft[field];
    return (dv === null ? undefined : dv) as InvoicePatch[K];
  };

  const advertiser = v('advertiser', invoice.advertiser) as string;
  const campaignId = v('campaignOrderId', invoice.campaignOrderId) as string | undefined;
  const gamOrderNumber = v('gamOrderNumber', invoice.gamOrderNumber) as string | undefined;
  // What GAM auto-link will actually match on (mirrors backend gamOrderToken):
  // the explicit GAM Order #, or the Campaign ID only if it's itself an "O-…" value.
  const gamToken =
    gamOrderNumber?.trim() ||
    (campaignId && /^o-/i.test(campaignId.trim()) ? campaignId.trim() : undefined);
  // "Why not synced" reason: prefer the live auto-link diagnostic from this
  // session; fall back to the reason persisted on the doc by the server-side
  // auto-link so cold loads and view-only users still see the cause.
  const effectiveDiagnostic = diagnostic ?? invoice.gam.unmatchedReason;
  const contracted = v('contractedImpressions', invoice.contractedImpressions) as number | undefined;
  const planned = v('plannedTotal', invoice.plannedTotal) as number | undefined;
  const billable = v('billableTotal', invoice.billableTotal) as number | undefined;
  const delivered3P = v('impressionsDelivered3P', invoice.delivery.impressionsDelivered3P) as number | undefined;
  // Rate honors an in-flight draft edit (EditForm edits rates[0]) else the stored rate line.
  const rate = draft.rates !== undefined ? draft.rates[0] : invoice.rates[0];
  const status = v('status', invoice.status) as string | undefined;
  const sourceOfTruth = (v('deliverySourceOfTruth', invoice.deliverySourceOfTruth) as '1P' | '3P') ?? '1P';
  // The STORED source of truth (draft-independent) — quick controls and the GAM
  // gates follow what the backend currently enforces, not an unsaved draft.
  const storedThreeP = isThreeP(invoice);

  const provDot = invoice.source === 'manual' ? styles.dotManual : styles.dotAuto;
  // Per-month invoice gate: this month must be CONFIRMED and priced (siblings independent).
  const canGenerate = canBill && isConfirmed(invoice.status) && invoice.billableTotal != null;

  // GAM-linked 1P rows hide their delivered/pacing figures until the month-true
  // report has run for the CURRENT link — the lifetime fallback overstates
  // multi-month campaigns, so partial GAM data is never rendered.
  const syncState = gamSyncState(invoice);
  const gamPending = syncState !== 'ready';

  return (
    <>
      <tr
        className={`inv-row${expanded ? ' inv-row-expanded' : ''}${isDirty ? ' inv-row-dirty' : ''}`}
        style={styles.row}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${advertiser || 'Invoice'} — ${invoice.monthKey}: ${expanded ? 'collapse' : 'expand'} details`}
        onKeyDown={(e) => {
          // Only when the row itself is focused — keystrokes inside the 3P
          // input/switch (or any other control) must not toggle expansion.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td className="inv-sticky-1" style={styles.tdChevron} aria-hidden="true">
          {expanded ? '▼' : '▶'}
        </td>
        <td className="inv-sticky-2" style={styles.td}>
          <div style={styles.advCell}>
            <span style={{ ...styles.dot, ...provDot }} title={`Source: ${invoice.source}`} />
            <span style={styles.advName}>{advertiser || '—'}</span>
            {invoice.needsReview && <span style={styles.reviewBadge} title="Auto-extracted — needs review">REVIEW</span>}
            {sourceOfTruth === '3P' && <span style={styles.threePBadge} title="3P (DV/IAS) is source of truth">3P</span>}
          </div>
          {invoice.agency && <div style={styles.subtle}>{invoice.agency}</div>}
        </td>
        <td style={styles.td}>
          {gamToken ? (
            <span title="GAM Order # — used to auto-link delivery/pacing">{gamToken}</span>
          ) : (
            <span style={styles.subtle} title="No GAM Order # — auto-link can't match this row yet. Backfill from IO or paste the O-… order # in edit mode.">
              no GAM #
            </span>
          )}
          {campaignId && campaignId !== gamToken && (
            <div style={styles.subtle} title="Internal Campaign ID (not used for GAM matching)">{campaignId}</div>
          )}
        </td>
        <td style={styles.td}>
          {invoice.ioLink ? (
            <a href={invoice.ioLink} target="_blank" rel="noopener noreferrer" style={styles.link} onClick={(e) => e.stopPropagation()}>
              IO ↗
            </a>
          ) : (
            '—'
          )}
        </td>
        <td style={{ ...styles.td, whiteSpace: 'nowrap' }} title={fmtDateRange(invoice.monthlyFlightStart, invoice.monthlyFlightEnd)}>
          {fmtDateRange(invoice.monthlyFlightStart, invoice.monthlyFlightEnd)}
        </td>
        <td style={{ ...styles.td, whiteSpace: 'nowrap' }} title={fmtDateRange(invoice.totalCampaignFlightStart, invoice.totalCampaignFlightEnd)}>
          {fmtDateRange(invoice.totalCampaignFlightStart, invoice.totalCampaignFlightEnd)}
        </td>
        <td style={styles.tdRight}>{fmtRate(rate)}</td>
        <td style={styles.tdRight}>
          {fmtInt(contracted)}
          {(invoice.plannedLineItems?.length ?? 0) >= 2 && (
            <div
              style={styles.subtle}
              title={invoice
                .plannedLineItems!.map((p) => `${p.label || 'Line item'} — ${fmtInt(p.plannedImpressions)}`)
                .join('\n')}
            >
              {invoice.plannedLineItems!.length} line items
            </div>
          )}
        </td>
        <td
          style={{ ...styles.tdRight, ...(storedThreeP ? styles.frozenCell : {}) }}
          title={
            storedThreeP
              ? 'GAM 1P — frozen while 3P verification is on (no longer fetched)'
              : gamPending
                ? 'Hidden while this month’s GAM figures sync — lifetime totals would overstate multi-month campaigns.'
                : invoice.delivery.impressionsDeliveredMonth !== undefined
                  ? `Delivered THIS MONTH across all linked line items (date-ranged GAM report` +
                    `${invoice.delivery.monthDeliveredAsOf ? `, ${fmtAsOf(invoice.delivery.monthDeliveredAsOf)}` : ''}).` +
                    `${
                      invoice.delivery.impressionsDelivered1P !== undefined
                        ? ` Lifetime across this month's line items: ${fmtInt(invoice.delivery.impressionsDelivered1P)}.`
                        : ''
                    }`
                  : undefined
          }
        >
          {gamPending ? (
            <span style={styles.subtle}>—</span>
          ) : (
            // The month-true figure IS the correctly-summed total of the linked
            // line items' delivery this month; the lifetime sum (tooltip) reads
            // 2-4× too high on multi-month campaigns.
            fmtInt(invoice.delivery.impressionsDeliveredMonth ?? invoice.delivery.impressionsDelivered1P)
          )}
        </td>
        {/* 3P Delivered cell: quick toggle (3P verification on/off) + inline
            entry of the agency-confirmed number when on. Click events stop
            here so using the controls doesn't expand the row. */}
        <td style={styles.tdRight} onClick={(e) => e.stopPropagation()}>
          <ThreePCell
            threeP={storedThreeP}
            value={delivered3P}
            disabled={!canManage || saving || isDirty}
            busy={saving}
            dirtyBlocked={isDirty}
            onToggle3P={(next) => {
              void onQuickPatch({ deliverySourceOfTruth: next ? '3P' : '1P' });
            }}
            // null = cleared (serialized in the PATCH so the backend deletes it),
            // matching the draft-edit convention in EditForm's setNum.
            onCommit={(n) => onQuickPatch({ impressionsDelivered3P: n } as InvoicePatch)}
          />
        </td>
        <td style={styles.td}>
          {gamPending ? (
            <SyncChip
              state={syncState}
              error={invoice.delivery.monthDeliveredError?.message}
              onRetry={canBill ? onRefreshDelivered : undefined}
              retryDisabled={refreshingDelivered}
            />
          ) : (
            <>
              <PacingBar
                delivery={invoice.delivery}
                variant="compact"
                noDataLabel={storedThreeP ? 'Awaiting 3P number' : undefined}
              />
              {!storedThreeP && <PacingGlance delivery={invoice.delivery} />}
            </>
          )}
          {!gamPending &&
            invoice.delivery.percentDelivered === undefined &&
            !storedThreeP &&
            (canManage && onAcceptSuggestion && suggestion && invoice.gam.gamLineItemIds.length === 0 ? (
              // A confirmable single-order match: name + one-click Link. Replaces
              // the passive "possible match" text that no one could act on.
              <div
                style={{ marginTop: 3, maxWidth: 170 }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div
                  title={suggestion.reason}
                  style={{
                    fontSize: 10.5,
                    color: 'var(--fg-3)',
                    lineHeight: 1.25,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  Match: {suggestion.gamOrderName ?? `GAM order ${suggestion.gamOrderId}`}
                </div>
                <button
                  type="button"
                  onClick={onAcceptSuggestion}
                  disabled={saving}
                  aria-label={`Link ${advertiser || 'invoice'} to GAM order ${suggestion.gamOrderName ?? suggestion.gamOrderId ?? ''}`}
                  style={{
                    marginTop: 2,
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 5,
                    border: '1px solid var(--accent)',
                    background: 'transparent',
                    color: 'var(--accent)',
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Linking…' : 'Link'}
                </button>
              </div>
            ) : effectiveDiagnostic ? (
              <div
                title={effectiveDiagnostic}
                style={{
                  fontSize: 10.5,
                  color: 'var(--fg-4)',
                  marginTop: 3,
                  maxWidth: 150,
                  lineHeight: 1.25,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {effectiveDiagnostic}
              </div>
            ) : null)}
        </td>
        <td style={styles.tdRight}>{fmtMoney(planned)}</td>
        <td style={styles.tdRight}>{fmtMoney(billable)}</td>
        <td style={styles.td}>
          {status ? (
            <StatusPill variant={statusVariant(status)} size="sm">
              {status}
            </StatusPill>
          ) : (
            '—'
          )}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={colCount} style={styles.detailTd}>
            <div style={styles.detail}>
              {editMode ? (
                <EditForm
                  invoice={invoice}
                  draft={draft}
                  canBill={canBill}
                  sourceOfTruth={sourceOfTruth}
                  onDraftChange={onDraftChange}
                />
              ) : (
                <ReadView invoice={invoice} />
              )}

              <div style={styles.pacingSection}>
                <div style={styles.sectionLabel}>Delivery & pacing</div>
                <DeliveryPacingPanel
                  invoice={invoice}
                  threeP={storedThreeP}
                  noDataLabel={storedThreeP ? 'Awaiting 3P number — enter it in the 3P Delivered column' : undefined}
                />
              </div>

              <div style={styles.detailFooter}>
                <div style={styles.metaLine}>
                  Source: <strong>{invoice.source}</strong>
                  {invoice.gam.matchMethod !== 'none' && ` · GAM match: ${invoice.gam.matchMethod}`}
                  {invoice.auditLog && invoice.auditLog.length > 0 && ` · ${invoice.auditLog.length} edit(s)`}
                </div>
                <div style={styles.footerActions}>
                  {invoice.generatedInvoice && (
                    <span style={styles.invoicedNum} title={`Invoiced — ${invoice.generatedInvoice.invoiceNumber}`}>
                      {invoice.generatedInvoice.invoiceNumber}
                    </span>
                  )}
                  {invoice.generatedInvoice && onDownload && (
                    <button className="inv-btn" style={styles.ghostBtn} onClick={onDownload} disabled={saving}>
                      Download
                    </button>
                  )}
                  {/* One-click confirm — the accountant's core action. Saves the
                      status quick-patch directly (no edit mode needed). */}
                  {canBill && !isConfirmed(invoice.status) && (
                    <button
                      className="inv-btn"
                      style={styles.confirmBtn}
                      onClick={() => void onQuickPatch({ status: 'CONFIRMED' })}
                      disabled={saving || isDirty}
                      title={
                        isDirty
                          ? 'Save or revert this row’s edits first'
                          : 'Mark this month CONFIRMED — you’ve verified delivered impressions match the contracted goal. With a Billable Total set, this unlocks invoice generation.'
                      }
                    >
                      {saving ? 'Saving…' : 'Mark CONFIRMED'}
                    </button>
                  )}
                  {canBill && onRefreshDelivered && (
                    <button
                      className="inv-btn"
                      style={styles.ghostBtn}
                      onClick={onRefreshDelivered}
                      disabled={saving || refreshingDelivered || !invoice.gam.gamLineItemIds.length || storedThreeP}
                      title={
                        storedThreeP
                          ? 'This row is 3P-verified — monthly delivered comes from the agency number, not GAM.'
                          : invoice.gam.gamLineItemIds.length
                            ? 'Pull this month’s delivered impressions from GAM (runs in the background)'
                            : 'Link this invoice to a GAM order first'
                      }
                    >
                      {refreshingDelivered ? 'Refreshing…' : 'Refresh delivered'}
                    </button>
                  )}
                  {canBill && onGenerate && (
                    <button
                      className="inv-btn"
                      style={canGenerate && !isDirty ? styles.genBtn : styles.genBtnDisabled}
                      disabled={!canGenerate || isDirty || generating || saving}
                      title={
                        isDirty
                          ? 'Save your edits before generating'
                          : canGenerate
                            ? 'Generate this month’s invoice PDF'
                            : !isConfirmed(invoice.status)
                              ? 'This month must be CONFIRMED first'
                              : 'Set this month’s Billable Total first'
                      }
                      onClick={onGenerate}
                    >
                      {generating ? 'Generating…' : invoice.generatedInvoice ? 'Regenerate' : 'Generate invoice'}
                    </button>
                  )}
                  {invoice.needsReview && (
                    <button className="inv-btn" style={styles.reviewBtn} onClick={onMarkReviewed} disabled={saving}>
                      Mark reviewed
                    </button>
                  )}
                  {editMode && isDirty && (
                    <>
                      <button className="inv-btn" style={styles.revertBtn} onClick={onRevertRow} disabled={saving}>
                        Revert
                      </button>
                      <button className="inv-btn" style={styles.saveBtn} onClick={onSaveRow} disabled={saving}>
                        {saving ? 'Saving…' : 'Save row'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ---------- 3P quick controls (main-row cell) ----------

/**
 * The "3P Delivered" cell: a switch that marks this IO as verified by a
 * 3rd-party auditing company (DV/IAS) instead of GAM, plus — when on — an
 * inline input for the agency-confirmed number. Commits on Enter/blur,
 * Escape reverts. Both controls PATCH immediately (no draft).
 */
const ThreePCell: React.FC<{
  threeP: boolean;
  value?: number;
  disabled: boolean;
  /** True while this row's quick PATCH is in flight (dims the controls). */
  busy?: boolean;
  /** True when the row has unsaved edit-mode drafts (quick controls pause). */
  dirtyBlocked: boolean;
  onToggle3P: (next: boolean) => void;
  /** Resolves false when the PATCH failed (the cell reverts to the saved value). */
  onCommit: (n: number | null) => Promise<boolean>;
}> = ({ threeP, value, disabled, busy, dirtyBlocked, onToggle3P, onCommit }) => {
  const stored = value === undefined ? '' : String(Math.round(value));
  const [text, setText] = React.useState(stored);
  // Re-sync the local text whenever the saved value changes (post-PATCH refresh).
  React.useEffect(() => setText(stored), [stored]);

  const revert = () => setText(stored);
  const commit = async () => {
    // Guard: an input that turns disabled while focused (row went dirty, save
    // started) still fires blur — that must not sneak a PATCH past the lockout.
    if (disabled) {
      revert();
      return;
    }
    const trimmed = text.trim().replace(/,/g, '');
    if (trimmed === '') {
      // A failed save must not leave the cell LOOKING saved — revert to truth.
      if (value !== undefined && !(await onCommit(null))) revert();
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) {
      revert();
      return;
    }
    if (value === undefined || Math.round(n) !== Math.round(value)) {
      if (!(await onCommit(Math.round(n)))) revert();
    } else {
      // No-op commit (e.g. "5000.4" over a stored 5000 rounds equal): snap the
      // text back to the stored value so the cell never DISPLAYS an unsaved
      // number as if it were saved.
      revert();
    }
  };

  return (
    <div className={busy ? 'inv-3p-busy' : undefined} style={styles.threePCellWrap} aria-busy={busy || undefined}>
      {threeP ? (
        <input
          className="inv-3p-input"
          type="text"
          inputMode="numeric"
          value={text}
          placeholder="3P #"
          aria-label="3P delivered impressions (agency-confirmed)"
          title={
            dirtyBlocked
              ? 'Save or revert this row’s edits first'
              : 'Enter the agency’s 3rd-party (DV/IAS) confirmed impressions — saves on Enter or click-away'
          }
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            else if (e.key === 'Escape') {
              revert();
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      ) : (
        <span>{fmtInt(value)}</span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={threeP}
        className="inv-3p-switch"
        aria-label="Confirm impressions via 3rd-party auditor instead of GAM"
        title={
          dirtyBlocked
            ? 'Save or revert this row’s edits first'
            : threeP
              ? '3P verification ON — GAM impressions are not fetched for this IO. Click to switch back to GAM (1P).'
              : 'Turn on if this IO’s impressions are confirmed by a 3rd-party auditor (DV/IAS) — stops GAM fetching and unlocks manual 3P entry.'
        }
        disabled={disabled}
        onClick={() => onToggle3P(!threeP)}
      >
        <span className="inv-3p-knob" />
      </button>
    </div>
  );
};

// ---------- "Syncing GAM…" chip ----------

/**
 * Shown in place of the pacing figures while a row's month-true GAM data is
 * pending, so partial / lifetime figures are never mistaken for the month's
 * delivery. Three states: a report is running (spinner), waiting for the next
 * scheduled sync, or the last refresh failed (click to retry).
 */
const SyncChip: React.FC<{
  state: Exclude<GamSyncState, 'ready'>;
  error?: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
}> = ({ state, error, onRetry, retryDisabled }) => {
  if (state === 'failed') {
    return (
      <button
        type="button"
        className="inv-sync-chip inv-sync-chip--failed"
        title={`Last GAM sync failed${error ? `: ${error}` : ''}${onRetry && !retryDisabled ? ' — click to retry.' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onRetry?.();
        }}
        disabled={!onRetry || retryDisabled}
      >
        ⚠ GAM sync failed{onRetry ? ' ↻' : ''}
      </button>
    );
  }
  return (
    <span
      className="inv-sync-chip"
      role="status"
      title={
        state === 'syncing'
          ? 'Fetching this month’s delivered impressions from GAM — the report can take a few minutes. Figures appear when it completes.'
          : 'This row’s month figures compute on the next GAM sync. Numbers are hidden until then so partial data is never shown.'
      }
    >
      {state === 'syncing' && <Spinner size={12} />}
      {state === 'syncing' ? 'Syncing GAM…' : 'Awaiting GAM sync'}
    </span>
  );
};

// ---------- Read-only detail ----------

const ReadView: React.FC<{ invoice: Invoice }> = ({ invoice }) => (
  <div style={styles.fieldGrid}>
    <Field label="GAM Order # (O-…)" value={invoice.gamOrderNumber || '— (not linkable yet)'} />
    <Field label="Campaign ID (CP…)" value={invoice.campaignOrderId || '—'} />
    <Field label="Description" value={invoice.description || '—'} wide />
    <Field label="Total campaign flight" value={fmtDateRange(invoice.totalCampaignFlightStart, invoice.totalCampaignFlightEnd)} />
    <Field
      label="Delivered (this month)"
      value={
        invoice.deliverySourceOfTruth === '3P'
          ? invoice.delivery.impressionsDelivered3P != null
            ? `${fmtInt(invoice.delivery.impressionsDelivered3P)} · 3P (agency-confirmed)`
            : '— (awaiting the agency’s 3P number)'
          : invoice.delivery.impressionsDeliveredMonth != null
            ? `${Math.round(invoice.delivery.impressionsDeliveredMonth).toLocaleString()}${
                invoice.delivery.monthDeliveredAsOf
                  ? ` · ${fmtAsOf(invoice.delivery.monthDeliveredAsOf)}`
                  : ''
              }`
            : invoice.monthRefreshInFlight
              ? '— (syncing from GAM…)'
              : '— (use “Refresh delivered”)'
      }
    />
    <Field label="Rates" value={invoice.rates.length ? invoice.rates.map((r) => `${r.label}: ${r.cpm ?? r.flatRate ?? '—'}`).join(', ') : '—'} />
    <Field label="Revised status" value={invoice.revisedStatus || '—'} />
    <Field label="Notes" value={invoice.notes || '—'} wide />
  </div>
);

const Field: React.FC<{ label: string; value: React.ReactNode; wide?: boolean }> = ({ label, value, wide }) => (
  <div style={{ ...styles.field, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
    <div style={styles.fieldLabel}>{label}</div>
    <div style={styles.fieldValue}>{value}</div>
  </div>
);

// ---------- Editable detail form ----------

const EditForm: React.FC<{
  invoice: Invoice;
  draft: InvoicePatch;
  canBill: boolean;
  sourceOfTruth: '1P' | '3P';
  onDraftChange: (field: keyof InvoicePatch, value: unknown) => void;
}> = ({ invoice, draft, canBill, sourceOfTruth, onDraftChange }) => {
  // A draft value of null (cleared) renders as empty via `?? ''`.
  const text = (field: keyof InvoicePatch, stored: unknown) =>
    ((field in draft ? draft[field] : stored) ?? '') as string;
  const numv = (field: keyof InvoicePatch, stored: unknown) =>
    ((field in draft ? draft[field] : stored) ?? '') as number | string;

  const setText = (field: keyof InvoicePatch) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onDraftChange(field, e.target.value);
  // Empty → null (not undefined) so the cleared field is serialized in the PATCH
  // body and the backend can delete it, rather than the key being dropped.
  const setNum = (field: keyof InvoicePatch) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onDraftChange(field, e.target.value === '' ? null : Number(e.target.value));

  const threeP = sourceOfTruth === '3P';
  const rateCpm = draft.rates !== undefined ? draft.rates[0]?.cpm : invoice.rates[0]?.cpm;

  return (
    <div style={styles.fieldGrid}>
      <Input label="Advertiser" value={text('advertiser', invoice.advertiser)} onChange={setText('advertiser')} />
      <Input label="Agency" value={text('agency', invoice.agency)} onChange={setText('agency')} />
      <Input label="Campaign ID (CP…)" value={text('campaignOrderId', invoice.campaignOrderId)} onChange={setText('campaignOrderId')} />
      <Input
        label="GAM Order # (O-…)"
        value={text('gamOrderNumber', invoice.gamOrderNumber)}
        onChange={setText('gamOrderNumber')}
        placeholder="e.g. O-D2QI57K — paste, then Auto-link GAM"
      />
      <Input label="IO link" value={text('ioLink', invoice.ioLink)} onChange={setText('ioLink')} />
      <Input label="Description" value={text('description', invoice.description)} onChange={setText('description')} wide />

      <Input type="date" label="Monthly flight start" value={text('monthlyFlightStart', invoice.monthlyFlightStart)} onChange={setText('monthlyFlightStart')} />
      <Input type="date" label="Monthly flight end" value={text('monthlyFlightEnd', invoice.monthlyFlightEnd)} onChange={setText('monthlyFlightEnd')} />
      <Input type="date" label="Total campaign start" value={text('totalCampaignFlightStart', invoice.totalCampaignFlightStart)} onChange={setText('totalCampaignFlightStart')} />
      <Input type="date" label="Total campaign end" value={text('totalCampaignFlightEnd', invoice.totalCampaignFlightEnd)} onChange={setText('totalCampaignFlightEnd')} />

      <Input
        type="number"
        label="Rate (CPM)"
        value={rateCpm ?? ''}
        onChange={(e) => {
          // Edit rates[0]'s CPM IN PLACE — replacing the whole array would
          // silently drop rates[1..] (multi-rate packaged buys from the sheet
          // import) and rates[0]'s label/flatRate on save.
          const first = draft.rates?.[0] ?? invoice.rates[0] ?? { label: 'Rate' };
          const rest = (draft.rates ?? invoice.rates).slice(1);
          onDraftChange('rates', [
            { ...first, cpm: e.target.value === '' ? undefined : Number(e.target.value) },
            ...rest,
          ]);
        }}
      />
      <Input type="number" label="Contracted impressions" value={numv('contractedImpressions', invoice.contractedImpressions)} onChange={setNum('contractedImpressions')} />
      <Input type="number" label="Planned total ($)" value={numv('plannedTotal', invoice.plannedTotal)} onChange={setNum('plannedTotal')} />

      {/* 3P source-of-truth toggle (the table row has a quick switch for this too) */}
      <div style={styles.field}>
        <div style={styles.fieldLabel}>3P is source of truth</div>
        <label style={styles.checkRow}>
          <input
            type="checkbox"
            checked={threeP}
            onChange={(e) => onDraftChange('deliverySourceOfTruth', e.target.checked ? '3P' : '1P')}
          />
          <span style={styles.subtle}>
            Impressions confirmed by a 3rd-party auditor (DV/IAS) — GAM is not fetched; enter the agency number
          </span>
        </label>
      </div>
      {/* 3P delivered is always editable — Ad Ops/Accounting record the agency-
          confirmed DV/IAS number for variance regardless of which source bills.
          The checkbox above only selects which number drives pacing/over-delivery. */}
      <Input
        type="number"
        label="3P delivered (DV, IAS)"
        value={numv('impressionsDelivered3P', invoice.delivery.impressionsDelivered3P)}
        onChange={setNum('impressionsDelivered3P')}
        placeholder="Agency-confirmed DV/IAS impressions"
      />

      <Input
        label="GAM line item IDs (comma-separated)"
        value={(draft.gamLineItemIds !== undefined ? draft.gamLineItemIds : invoice.gam.gamLineItemIds).join(', ')}
        onChange={(e) =>
          onDraftChange(
            'gamLineItemIds',
            e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
          )
        }
        wide
      />

      {/* Accountant-only fields */}
      <Input type="number" label={`Billable total ($)${canBill ? '' : ' 🔒'}`} value={numv('billableTotal', invoice.billableTotal)} onChange={setNum('billableTotal')} disabled={!canBill} />
      <Input label={`Status${canBill ? '' : ' 🔒'}`} value={text('status', invoice.status)} onChange={setText('status')} disabled={!canBill} />
      <Input label="Revised status" value={text('revisedStatus', invoice.revisedStatus)} onChange={setText('revisedStatus')} />
      <TextArea label="Notes" value={text('notes', invoice.notes)} onChange={setText('notes')} />
    </div>
  );
};

const Input: React.FC<{
  label: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  wide?: boolean;
  placeholder?: string;
}> = ({ label, value, onChange, type = 'text', disabled, wide, placeholder }) => (
  <div style={{ ...styles.field, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
    <label style={styles.fieldLabel}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      style={{ ...styles.input, ...(disabled ? styles.inputDisabled : {}) }}
    />
  </div>
);

const TextArea: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}> = ({ label, value, onChange }) => (
  <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
    <label style={styles.fieldLabel}>{label}</label>
    <textarea value={value} onChange={onChange} rows={2} style={{ ...styles.input, resize: 'vertical' }} />
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  // Row borders + state backgrounds live in Invoicing.css (.inv-row*) so the
  // sticky columns stay opaque; only behavior styling stays inline.
  row: { cursor: 'pointer' },
  td: { padding: '10px 12px', verticalAlign: 'middle', color: 'var(--fg-1)', fontSize: 13 },
  tdRight: {
    padding: '10px 12px',
    verticalAlign: 'middle',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--fg-1)',
    fontSize: 13,
  },
  tdChevron: { padding: '10px 8px', color: 'var(--fg-4)', fontSize: 10, width: 24 },
  advCell: { display: 'flex', alignItems: 'center', gap: 6 },
  advName: { fontWeight: 600 },
  dot: { width: 7, height: 7, borderRadius: 'var(--radius-pill)', flexShrink: 0, display: 'inline-block' },
  dotManual: { background: 'var(--accent)' },
  dotAuto: { background: 'var(--fg-4)' },
  reviewBadge: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.05em',
    color: 'var(--warning)',
    background: 'var(--warning-soft)',
    padding: '1px 5px',
    borderRadius: 4,
  },
  threePBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: 'var(--accent)',
    background: 'var(--accent-soft)',
    padding: '1px 5px',
    borderRadius: 4,
  },
  subtle: { color: 'var(--fg-4)', fontSize: 12 },
  frozenCell: { color: 'var(--fg-4)' },
  threePCellWrap: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 },
  link: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 },
  // border-bottom needed here: with border-collapse:separate the row borders
  // live on `.inv-row td`, and the detail row isn't an .inv-row.
  detailTd: {
    padding: 0,
    background: 'var(--bg-surface-3)',
    borderBottom: '1px solid var(--border-1)',
  },
  detail: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 18 },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  fieldLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  fieldValue: { fontSize: 13, color: 'var(--fg-1)', wordBreak: 'break-word' },
  input: {
    padding: '7px 9px',
    fontSize: 13,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputDisabled: { background: 'var(--bg-surface-3)', color: 'var(--fg-4)', cursor: 'not-allowed' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 },
  pacingSection: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--fg-3)',
  },
  detailFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    borderTop: '1px solid var(--border-1)',
    paddingTop: 12,
  },
  metaLine: { fontSize: 11.5, color: 'var(--fg-4)' },
  footerActions: { display: 'flex', gap: 8 },
  saveBtn: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  revertBtn: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  reviewBtn: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'var(--warning-soft)',
    color: 'var(--warning)',
    border: '1px solid var(--warning)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  genBtn: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  genBtnDisabled: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'var(--bg-surface-3)',
    color: 'var(--fg-4)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    cursor: 'not-allowed',
  },
  confirmBtn: {
    padding: '7px 14px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'var(--success-soft)',
    color: 'var(--success)',
    border: '1px solid var(--success)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  ghostBtn: {
    padding: '7px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  invoicedNum: {
    fontSize: 11.5,
    fontWeight: 700,
    color: 'var(--success)',
    alignSelf: 'center',
    fontVariantNumeric: 'tabular-nums',
  },
};

export default InvoiceRow;
